import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import DeleteIcon from '@/assets/all-icons/delete-icon';
import useTranslations from '@/i18n/useTranslations';
import GalleryService from '@/services/gallery.service';
import ContinueButton from '@/shared-components/buttons/components/ContinueButton';
import UserProfileImage from '@/shared-components/cards/components/UserProfileImage';
import CalendarIcon from '@/shared-components/icons/components/forms/CalendarIcon';
import PublicSuccessCheck from '@/shared-components/icons/components/public/PublicSuccessCheck';
import XIcon from '@/shared-components/icons/components/public/XIcon';
import PopupToBottomSheet from '@/shared-components/popups/components/PopupToBottomSheet';
import Text from '@/shared-components/text/components/Text';
import { resolveLocalizedPublicPath } from '@/utils/localePublicUrl';

import './components/styles/SellInStorePopup.css';

const SELL_IN_STORE_COMMISSION = 0;
function getDefaultDate() {
  return new Date().toISOString().slice(0, 10);
}

export default function SellInStorePopup({ artworkData, open, close, onSuccess = null }) {
  const navigate = useNavigate();
  const t = useTranslations('common.shared.artwork_view.sell_in_store');

  const [contactSearch, setContactSearch] = useState('');
  const [contacts, setContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [contactsError, setContactsError] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);
  const [saleDate, setSaleDate] = useState(getDefaultDate());
  const [salePrice, setSalePrice] = useState('');
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [resultType, setResultType] = useState(null);
  const saleDateInputRef = useRef(null);

  // const artistName = artworkData?.artwork_about?.artwork_artist?.user_name
  //   || artworkData?.artwork_about?.artwork_gallery_artist?.artist_name
  //   || 'the artist';
  // const galleryName = artworkData?.artwork_about?.artwork_gallery?.user_name || 'your gallery';

  const canSubmit = useMemo(() => {
    if (!acceptTerms || !saleDate) {
      return false;
    }
    return Boolean(selectedContact);
  }, [acceptTerms, saleDate, selectedContact]);

  const contactResults = useMemo(() => {
    const query = contactSearch.trim().toLowerCase();
    const resolveTitle = (contact) => {
      if (contact?.business_name?.trim()) return contact.business_name.trim();
      const fullName = `${contact?.first_name ?? ''} ${contact?.last_name ?? ''}`.trim();
      if (fullName) return fullName;
      if (contact?.username?.trim()) return contact.username.trim();
      return contact?.email ?? t('fallback_contact');
    };
    const enriched = contacts.map((contact) => ({
      ...contact,
      title: resolveTitle(contact),
    }));
    if (!query) return enriched;
    return enriched.filter((contact) => (
      [contact.title, contact.email, contact.username, contact.business_id]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(query))
    ));
  }, [contacts, contactSearch, t]);

  useEffect(() => {
    if (!open) {
      return;
    }

    setContactSearch('');
    setContacts([]);
    setContactsError(null);
    setContactsLoading(false);
    setSelectedContact(null);
    setIsSearchOpen(false);
    setSaleDate(getDefaultDate());
    const defaultPrice = artworkData?.artwork_about?.artwork_price;
    setSalePrice(
      defaultPrice === undefined || defaultPrice === null ? '' : String(defaultPrice),
    );
    setAcceptTerms(false);
    setSubmitting(false);
    setWithdrawing(false);
    setSubmitError(null);
    setResultType(null);
    const loadContacts = async () => {
      setContactsLoading(true);
      setContactsError(null);
      const { response, data } = await GalleryService.listGalleryContacts();
      if (!response.ok) {
        setContactsError(t('errors.load_contacts'));
        setContacts([]);
        setContactsLoading(false);
        return;
      }
      const list = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];
      setContacts(list);
      setContactsLoading(false);
    };

    loadContacts();
  }, [open]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!canSubmit || submitting) {
      return;
    }

    setSubmitting(true);
    setSubmitError(null);
    const normalizedSalePrice = salePrice.trim() === '' ? null : Number(salePrice);

    const payload = {
      profile_claimed: Boolean(selectedContact?.profile_claimed),
      collector_id: null,
      collector_email: selectedContact?.email?.trim() ?? null,
      contact_id: selectedContact?.id ?? selectedContact?._id ?? null,
      sold_at: saleDate,
      sale_price: Number.isFinite(normalizedSalePrice) ? normalizedSalePrice : null,
      sale_currency: artworkData?.artwork_about?.artwork_currency ?? 'EUR',
    };

    const { response, data } = await GalleryService.sellInStore(artworkData?._id, payload);

    if (!response.ok) {
      const backendMessage = data?.message || data?.error || t('errors.unexpected');
      setSubmitError(backendMessage);
      setSubmitting(false);
      return;
    }

    const updatedArtwork = data?.data ?? data?.artwork ?? null;
    if (updatedArtwork && typeof onSuccess === 'function') {
      onSuccess(updatedArtwork);
    }
    setResultType(selectedContact?.profile_claimed ? 'claimed' : 'invite');
    setSubmitting(false);
  };

  const handleWithdraw = async () => {
    if (withdrawing || submitting) {
      return;
    }
    if (!artworkData?._id) {
      setSubmitError(t('errors.missing_artwork'));
      return;
    }
    setWithdrawing(true);
    setSubmitError(null);
    const { response, data } = await GalleryService.withdrawArtwork(artworkData._id);
    if (!response.ok) {
      const backendMessage = data?.message || data?.error || t('errors.withdraw');
      setSubmitError(backendMessage);
      setWithdrawing(false);
      return;
    }
    setWithdrawing(false);
    close();
  };

  const renderHeader = () => (
    <div className="sell-in-store__header">
      <XIcon className="sell-in-store__close-icon" onClick={close} />

      <Text className="sell-in-store__title">
        {t('title')}
      </Text>
    </div>
  );

  const renderCollectorSearch = () => (
    <div className="sell-in-store__collector">
      <Text className="sell-in-store__section-label">
        {t('fields.gallery_contact')}
      </Text>

      {selectedContact ? (
        <div className="sell-in-store__collector-selected">
          <div className="sell-in-store__collector-pill">
            <UserProfileImage
              className="sell-in-store__collector-avatar"
              typeOfProfile="collector"
              image={null}
            />
            <div className="sell-in-store__collector-texts">
              <Text className="sell-in-store__collector-name">
                {selectedContact.business_name?.trim()
                  || `${selectedContact.first_name ?? ''} ${selectedContact.last_name ?? ''}`.trim()
                  || selectedContact.username
                  || selectedContact.email}
              </Text>
              {selectedContact.email && (
                <Text className="sell-in-store__collector-username">
                  {selectedContact.email}
                </Text>
              )}
            </div>
            <button
              type="button"
              className="sell-in-store__collector-remove"
              onClick={() => setSelectedContact(null)}
              aria-label={t('actions.remove_contact')}
            >
              <DeleteIcon className="sell-in-store__collector-remove-icon" />
            </button>
          </div>
        </div>
      ) : (
        <div className="sell-in-store__search-wrapper">
          <input
            type="search"
            className="sell-in-store__search-input"
            placeholder={t('search.placeholder')}
            value={contactSearch}
            onChange={(event) => setContactSearch(event.target.value)}
            onFocus={() => setIsSearchOpen(true)}
            onBlur={() => {
              setTimeout(() => setIsSearchOpen(false), 100);
            }}
            spellCheck={false}
          />
          {contactSearch && (
            <button
              type="button"
              className="sell-in-store__search-clear"
              aria-label={t('actions.clear_search')}
              onMouseDown={(event) => {
                event.preventDefault();
                setContactSearch('');
                setIsSearchOpen(true);
              }}
            >
              <XIcon />
            </button>
          )}
          {contactsLoading && (
            <span className="sell-in-store__search-spinner" aria-hidden="true" />
          )}
          {isSearchOpen && contactSearch && !contactsLoading && contactResults.length === 0 && !contactsError && (
            <Text className="sell-in-store__search-empty">
              {t('search.empty')}
            </Text>
          )}
          {isSearchOpen && contactsError && (
            <Text className="sell-in-store__error">
              {contactsError}
            </Text>
          )}
          {isSearchOpen && !contactsLoading && contactResults.length > 0 && (
            <ul className="sell-in-store__search-results">
              {contactResults.map((contact) => (
                <li key={contact.id ?? contact._id ?? contact.email}>
                  <button
                    type="button"
                    className="sell-in-store__search-result"
                    onMouseDown={(event) => {
                      event.preventDefault();
                      setSelectedContact(contact);
                      setContactSearch('');
                      setSubmitError(null);
                      setIsSearchOpen(false);
                    }}
                  >
                    <UserProfileImage
                      className="sell-in-store__collector-avatar"
                      typeOfProfile="collector"
                      image={null}
                    />
                    <div className="sell-in-store__collector-texts">
                      <Text className="sell-in-store__collector-name">
                        {contact.title}
                      </Text>
                      {(contact.email || contact.username) && (
                        <Text className="sell-in-store__collector-username">
                          {contact.email || contact.username}
                        </Text>
                      )}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );

  const renderDateField = () => (
    <div className="sell-in-store__date">
      <Text className="sell-in-store__section-label">
        {t('fields.date_sold')}
      </Text>
      <div className="sell-in-store__date-field">
        <input
          ref={saleDateInputRef}
          type="date"
          className="sell-in-store__date-input"
          value={saleDate}
          onChange={(event) => setSaleDate(event.target.value)}
          max={getDefaultDate()}
        />
        <button
          type="button"
          className="sell-in-store__calendar-button"
          aria-label={t('fields.date_sold')}
          onClick={() => {
            const input = saleDateInputRef.current;
            if (typeof input?.showPicker === 'function') {
              input.showPicker();
              return;
            }
            input?.focus();
            input?.click();
          }}
        >
          <CalendarIcon className="sell-in-store__calendar-icon" />
        </button>
      </div>
    </div>
  );

  const renderPriceField = () => (
    <div className="sell-in-store__price">
      <Text className="sell-in-store__section-label">
        {t('fields.final_price')}
      </Text>
      <div className="sell-in-store__price-field">
        <input
          type="number"
          className="sell-in-store__price-input"
          value={salePrice}
          onChange={(event) => setSalePrice(event.target.value)}
          placeholder="0.00"
          min="0"
          step="0.01"
        />
        <span className="sell-in-store__price-currency">
          {artworkData?.artwork_about?.artwork_currency ?? 'EUR'}
        </span>
      </div>
    </div>
  );

  const renderBenefits = () => (
    <div className="sell-in-store__benefits">
      <Text className="sell-in-store__section-label">
        {t('options.heading')}
      </Text>
      <div className="sell-in-store__benefits-content">
        <Text className="sell-in-store__benefits-intro">
          {t('options.intro')}
        </Text>
        <div className="sell-in-store__benefits-section">
          <Text className="sell-in-store__benefits-title">
            {t('options.suarte_title')}
          </Text>
          <Text className="sell-in-store__benefits-note">
            {t('options.suarte_note')}
          </Text>
          <ul className="sell-in-store__benefits-list">
            <li>
              <PublicSuccessCheck className="sell-in-store__benefits-icon" />
              <Text>{t('options.benefits.certificate')}</Text>
            </li>
            <li>
              <PublicSuccessCheck className="sell-in-store__benefits-icon" />
              <Text>{t('options.benefits.claim')}</Text>
            </li>
            <li>
              <PublicSuccessCheck className="sell-in-store__benefits-icon" />
              <Text>{t('options.benefits.secondary_market')}</Text>
            </li>
            <li>
              <PublicSuccessCheck className="sell-in-store__benefits-icon" />
              <Text>{t('options.benefits.royalties')}</Text>
            </li>
            <li>
              <PublicSuccessCheck className="sell-in-store__benefits-icon" />
              <Text>{t('options.benefits.commission')}</Text>
            </li>
            <li>
              <PublicSuccessCheck className="sell-in-store__benefits-icon" />
              <Text>{t('options.benefits.sold_works')}</Text>
            </li>
          </ul>
        </div>
        <div className="sell-in-store__benefits-section">
          <Text className="sell-in-store__benefits-title">
            Admin only ({t('options.admin_title_suffix')})
          </Text>
          <Text className="sell-in-store__benefits-note">
            {t('options.admin_note')}
          </Text>
        </div>
        <div className="sell-in-store__percentage">
          <Text className="sell-in-store__percentage-label">{t('options.percentage')}</Text>
          <Text className="sell-in-store__percentage-value">
            {SELL_IN_STORE_COMMISSION}%
          </Text>
        </div>
      </div>
    </div>
  );

  const renderTerms = () => (
    <label className="sell-in-store__terms">
      <input
        type="checkbox"
        checked={acceptTerms}
        onChange={(event) => setAcceptTerms(event.target.checked)}
      />
      <span>
        {t('terms.prefix')}{' '}
        <a
          href={resolveLocalizedPublicPath('/sell-in-store-terms')}
          target="_blank"
          rel="noreferrer"
        >
          {t('terms.link')}
        </a>
        .
      </span>
    </label>
  );

  const renderFooter = () => (
    <div className="sell-in-store__footer">
      {submitError && (
        <Text className="sell-in-store__error">
          {submitError}
        </Text>
      )}
      <div className="sell-in-store__footer-actions">
        <ContinueButton
          className="sell-in-store__submit continue-button_two"
          loading={withdrawing}
          turnOff={!canSubmit || submitting}
          type="button"
          onClick={handleWithdraw}
        >
          Admin only
        </ContinueButton>
        <ContinueButton
          className="sell-in-store__submit continue-button_two"
          loading={submitting}
          turnOff={!canSubmit || withdrawing}
          type="submit"
        >
          {t('actions.at_suarte')}
        </ContinueButton>
      </div>
    </div>
  );

  const renderConfirmation = () => (
    <div className="sell-in-store__confirmation">
      {renderHeader()}
      <div className="sell-in-store__confirmation-body">
        <Text className="sell-in-store__confirmation-title">
          {resultType === 'claimed'
            ? t('confirmation.claimed_title')
            : t('confirmation.invite_title')}
        </Text>
        <Text className="sell-in-store__confirmation-text" paragraph>
          {resultType === 'claimed'
            ? t('confirmation.claimed_text')
            : t('confirmation.invite_text')}
        </Text>
      </div>
      <div className="sell-in-store__confirmation-footer">
        {resultType === 'claimed' && selectedContact?.username ? (
          <ContinueButton
            className="sell-in-store__submit"
            onClick={() => {
              close();
              navigate(`/user/${selectedContact.username}`);
            }}
          >
            {t('actions.see_profile')}
          </ContinueButton>
        ) : (
          <ContinueButton
            className="sell-in-store__submit"
            onClick={close}
          >
            {t('actions.done')}
          </ContinueButton>
        )}
      </div>
    </div>
  );

  return (
    <PopupToBottomSheet
      className="sell-in-store__container"
      open={open}
      close={close}
      bottomSheetOptions={{
        backgroundClassName: 'sell-in-store__backdrop',
      }}
      popupOptions={{
        opacity: true,
        className: 'sell-in-store__popup',
      }}
    >
      {resultType ? (
        renderConfirmation()
      ) : (
        <form className="sell-in-store__form" onSubmit={handleSubmit}>
          {renderHeader()}
          <div className="sell-in-store__body">
            <Text className="sell-in-store__description" >
              {t('description')}
            </Text>
            <div className="sell-in-store__details-row">
              {renderDateField()}
              {renderPriceField()}
            </div>
            {renderCollectorSearch()}
            {renderBenefits()}
            {renderTerms()}
          </div>
          {renderFooter()}
        </form>
      )}
    </PopupToBottomSheet>
  );
}
